import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {CountryChartData} from '../../../../models/country-chart-data.interface';
import {Chart} from 'angular-highcharts';
import {PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {RETURN_TREND_BY_COUNTRY} from '../../../../../constants/charts';
import {MessageService} from '../../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../../core/services/auth.service';
import {formatNumber} from '../../../../utils/number-format-utils';
import {CountryChartInfo} from '../../../../models/country-chart-info.interface';

interface YearCountryData {
  year: string;
  countryData: CountryChartData[];
}

@Component({
  selector: 'app-return-trend-by-country-bar-chart',
  templateUrl: './return-trend-by-country-bar-chart.component.html',
  styleUrls: ['./return-trend-by-country-bar-chart.component.scss']
})
export class ReturnTrendByCountryBarChartComponent implements OnInit, OnChanges {

  @Input()
  data!: [CountryChartData[]];
  @Input()
  displayUnits!: string;
  @Input()
  title!: string;
  @Input()
  subtitle!: string;
  @Input()
  countries!: CountryChartInfo[];

  textBelow!: string;
  chart!: Chart;
  chartData!: SeriesOptionsType[];
  countryCodes!: string[];
  textBelowCode = 'kfcharts.Country.ReturnRate.TrendByCountry.textBelow';
  isAdmin!: boolean;

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    this.messageService.getDialogInfo(this.textBelowCode).subscribe((data) => {
      if (data !== null) {
        this.textBelow = data.description;
      }
    });
  }

  ngOnChanges(): void {
    if (this.data) {
      this.countryCodes = this.countries.map(c => c.code);

      const normalizedData = this.normalizeData(this.groupByYear(this.data), this.countries);

      // highcharts
      this.chartData = this.convertToChartData(normalizedData);
      this.chart = new Chart({
        chart: {
          zoomType: 'xy',
          type: 'column',
          options3d: {
            enabled: true,
            alpha: 7,
            beta: 0,
            depth: 35,
            viewDistance: 15
          }
        },
        colors: RETURN_TREND_BY_COUNTRY,
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        exporting: {
          tableCaption: this.title + ' ' + this.subtitle
        },
        tooltip: {
          formatter(): string[] | undefined {
            if (this.point.name) {
              const pointOptions = this.point as PointOptionsObject;
              if (pointOptions.custom?.absolute === 0) {
                return ['<div class="center"><b>' + pointOptions.custom?.title + '</b></div><br><br>',
                  '<i>No data available</i><br>',
                ];
              }
              return ['<div class="center"><b>' + pointOptions.custom?.title + '</b></div><br><br>',
                '- Put On Market: ' + formatNumber(pointOptions.custom?.absolute) + ' tons' + '<br>',
                '- Collected: ' + formatNumber(pointOptions.custom?.absoluteCollected) + ' tons' + '<br>',
                '- Return rate: ' + formatNumber((pointOptions.custom?.absoluteCollected / pointOptions.custom?.absolute) * 100) + ' %' + '<br>',
              ];
            }
            // If not null, use the default formatter
            // return tooltip.defaultFormatter.call(this, tooltip);
            return undefined;
          }
        },
        xAxis: {
          categories: this.countryCodes,
          title: {
            text: null
          }
        },
        yAxis: {
          labels: {
            format: '{value}%'
          }
        },
        plotOptions: {
          column: {
            minPointLength: 0.5
          }
        },
        series: this.chartData
      });
    }
  }

  editTextBelow(): void {
    this.router.navigate(
      ['/admin/chart-labels'],
      {state: {code: this.textBelowCode}}).then(
      () => {
        // do nothing
      }
    );
  }

  private normalizeData(groupedByYear: YearCountryData[], countryCodes: CountryChartInfo[]): YearCountryData[] {
    const normalizedData: YearCountryData[] = [];
    groupedByYear.forEach(item => {
      if (item.countryData.length === countryCodes.length) {
        normalizedData.push(item);
      } else {
        const newCountryData: CountryChartData[] = [];
        countryCodes.forEach(countryInfo => {
          let countryDataItem = item.countryData.find(c => c.countryCode === countryInfo.code);
          if (!countryDataItem) {
            countryDataItem = {
              countryCode: countryInfo.code,
              absolute: 0,
              year: Number(item.year),
              population: 0,
              countryName: countryInfo.name
            } as CountryChartData;
          }
          newCountryData.push(countryDataItem);
        });
        normalizedData.push({year: item.year, countryData: newCountryData});
      }
    });
    return normalizedData;
  }

  private convertToChartData(data: YearCountryData[]): SeriesOptionsType[] {
    this.chart?.ref?.hideData();
    const chartData = [] as SeriesOptionsType[];
    data.forEach(item => {
      chartData.push({
        name: item.year,
        type: 'column',
        data: item.countryData.map(dataItem => {
          return {
            y: Number(
              (dataItem?.absoluteCollected ? (dataItem?.absoluteCollected / dataItem.absolute) * 100 : dataItem.absolute).toFixed(2)),
            name: dataItem.countryName,
            custom: {
              absolute: dataItem.absolute,
              absoluteCollected: dataItem.absoluteCollected,
              title: (dataItem.countryName || 'No data available') + ' - ' + dataItem.year
            }
          };
        })
      });
    });
    return chartData;
  }

  private groupByYear(objectArray: Array<any>): YearCountryData[] {
    const yearCountryDataArray: YearCountryData[] = [];
    const groupedArray = objectArray.reduce((acc, obj) => {
      const key = obj[0].year;
      if (!acc[key]) {
        acc[key] = [];
      }
      // Add object to list for given key's value
      obj.forEach((item: any) => acc[key].push(item));
      return acc;
    }, {});

    Object.getOwnPropertyNames(groupedArray).forEach((key: string) => {
      const yearCountryData = {} as YearCountryData;
      yearCountryData.year = key;
      yearCountryData.countryData = groupedArray[key];
      yearCountryDataArray.push(yearCountryData);
    });

    return yearCountryDataArray;
  }

}
